import React, { useState, useEffect } from 'react';
import { AccountsList } from './AccountsList';
import { AccountForm } from './AccountForm';
import { EvolutionAccountsPanel } from './EvolutionAccountsPanel';
import { TelegramBotsPanel } from './TelegramBotsPanel';
import { waAccountsApi, igAccountsApi } from '../../utils/admin/accountsApi';
import { connectInstagramOAuth } from '../../utils/admin/instagramOauth';
import './AdminStyles.css';

interface Account {
  id: number;
  // WhatsApp
  phone?: string;
  phone_id?: string;
  bm_id?: string;
  bm_level?: string;
  // Instagram
  page_id?: string;
  instagram_account_id?: string;
  // comuns
  proxy_url?: string;
  is_active: boolean;
  createdAt?: string;
  created_at?: string;
}

type TabType = 'wa' | 'ig' | 'evolution' | 'telegram';

export const AdminDashboardRefactored: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('wa');
  const [waAccounts, setWaAccounts] = useState<Account[]>([]);
  const [igAccounts, setIgAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [editingAccount, setEditingAccount] = useState<Account | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);

  const loadAccounts = async (type: TabType, page: number = 0) => {
    // Evolution e Telegram têm painéis próprios e autocontidos (carregam seus dados internamente).
    if (type === 'evolution' || type === 'telegram') return;
    try {
      setLoading(true);
      setError('');

      const api = type === 'wa' ? waAccountsApi : igAccountsApi;
      const data = await api.list(page, 20);

      if (type === 'wa') {
        setWaAccounts(data.accounts);
      } else {
        setIgAccounts(data.accounts);
      }

      setCurrentPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar contas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAccounts(activeTab, 0);
  }, [activeTab]);

  const handleCreateAccount = async (formData: any) => {
    try {
      setSubmitting(true);
      setError('');

      const api = activeTab === 'wa' ? waAccountsApi : igAccountsApi;
      await api.create(formData);

      setSuccess(`Conta ${activeTab === 'wa' ? 'WhatsApp' : 'Instagram'} criada com sucesso`);
      setShowForm(false);
      await loadAccounts(activeTab, 0);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar conta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateAccount = async (formData: any) => {
    if (!editingAccount) return;

    try {
      setSubmitting(true);
      setError('');

      const api = activeTab === 'wa' ? waAccountsApi : igAccountsApi;
      await api.update(editingAccount.id, formData);

      setSuccess('Conta atualizada com sucesso');
      setEditingAccount(undefined);
      setShowForm(false);
      await loadAccounts(activeTab, currentPage);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar conta');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAccount = async (id: number) => {
    try {
      setError('');
      const api = activeTab === 'wa' ? waAccountsApi : igAccountsApi;
      await api.delete(id);

      setSuccess('Conta deletada com sucesso');
      await loadAccounts(activeTab, currentPage);

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao deletar conta');
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      setError('');
      const api = activeTab === 'wa' ? waAccountsApi : igAccountsApi;
      await api.toggle(id);

      await loadAccounts(activeTab, currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao alternar status');
    }
  };

  // Só WA: permite/bloqueia disparos do número.
  const handleToggleShots = async (id: number) => {
    try {
      setError('');
      await waAccountsApi.toggleShots(id);
      await loadAccounts(activeTab, currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao alternar disparos');
    }
  };

  const handleEditAccount = (account: Account) => {
    setEditingAccount(account);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingAccount(undefined);
  };

  const currentAccounts = activeTab === 'wa' ? waAccounts : igAccounts;

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Gerenciamento de Contas</h1>
      </div>

      {error && <div className="error-message">⚠️ {error}</div>}
      {success && <div className="success-message">✓ {success}</div>}

      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'wa' ? 'active' : ''}`}
          onClick={() => setActiveTab('wa')}
        >
          📱 WhatsApp
        </button>
        <button
          className={`tab-button ${activeTab === 'ig' ? 'active' : ''}`}
          onClick={() => setActiveTab('ig')}
        >
          📸 Instagram
        </button>
        <button
          className={`tab-button ${activeTab === 'evolution' ? 'active' : ''}`}
          onClick={() => setActiveTab('evolution')}
        >
          🤖 Evolution
        </button>
        <button
          className={`tab-button ${activeTab === 'telegram' ? 'active' : ''}`}
          onClick={() => setActiveTab('telegram')}
        >
          ✈️ Telegram
        </button>
      </div>

      {activeTab === 'evolution' ? (
        <EvolutionAccountsPanel />
      ) : activeTab === 'telegram' ? (
        <TelegramBotsPanel />
      ) : (
      <div className="tab-content">
        <div className="content-section">
          <h3 className="section-title">
            {activeTab === 'wa' ? 'Contas WhatsApp' : 'Contas Instagram'}
          </h3>

          {!showForm && (
            <button
              className="add-account-button"
              onClick={() => setShowForm(true)}
            >
              + Adicionar Conta
            </button>
          )}

          {!showForm && activeTab === 'ig' && (
            <>
              <button
                className="add-account-button"
                style={{ marginLeft: '8px' }}
                onClick={() => {
                  connectInstagramOAuth().catch((err) => {
                    console.error('Erro ao conectar Instagram:', err);
                    alert('Não foi possível iniciar a conexão com o Instagram. Tente novamente.');
                  });
                }}
                title="Abre o login do Instagram, salva o token e registra a conta (atrele o V8 e os números depois)"
              >
                📸 Conectar via Instagram
              </button>
              <p style={{ color: '#999', fontSize: '13px', marginTop: '8px' }}>
                Após conectar, a conta aparece aqui (inativa). Atualize a lista e edite para atrelar o V8 e os números de suporte.
              </p>
            </>
          )}

          <div style={{ marginTop: '20px' }}>
            <AccountsList
              type={activeTab}
              accounts={currentAccounts}
              loading={loading}
              onEdit={handleEditAccount}
              onDelete={handleDeleteAccount}
              onToggle={handleToggleStatus}
              onToggleShots={handleToggleShots}
            />
          </div>
        </div>

        <div className="content-section">
          {showForm && (
            <AccountForm
              type={activeTab}
              account={editingAccount}
              onSubmit={editingAccount ? handleUpdateAccount : handleCreateAccount}
              onCancel={handleCancel}
              loading={submitting}
            />
          )}

          {!showForm && (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
              <p>Selecione uma conta para editar ou clique em "Adicionar Conta"</p>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};
